import type { FacilityEquipment } from '@reliance/types/models/FacilityEquipment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';

type FormData = {
  id?: number;
  name: string;
  serialNumber: string;
  description: string;
  type: string;
};

export type AddEditModalProps = {
  facility: { id?: number } | null | undefined;
  show: boolean;
  editEquipment: FacilityEquipment | null | undefined;
  onClose(saved: boolean): void;
};

function formDataFromEdit(
  edit: FacilityEquipment | null | undefined
): FormData {
  if (edit == null) {
    return {
      name: '',
      serialNumber: '',
      description: '',
      type: '',
    };
  }
  return {
    id: edit.id,
    name: edit.name ?? '',
    serialNumber: edit.serialNumber ?? '',
    description: edit.description ?? '',
    type: edit.type ?? '',
  };
}

const AddEditModal: React.FC<AddEditModalProps> = ({
  facility,
  show,
  editEquipment,
  onClose,
}) => {
  const facilityId = facility?.id;
  const modalOpen = Boolean(show && facilityId != null);

  const [formData, setFormData] = useState<FormData>(() => formDataFromEdit(undefined));
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!modalOpen || facilityId == null) {
      return;
    }
    setIsEditing(editEquipment?.id != null);
    setFormData(formDataFromEdit(editEquipment));
    setSaving(false);
  }, [modalOpen, editEquipment, facilityId]);

  const setField = useCallback((patch: Partial<FormData>) => {
    setFormData((d) => ({ ...d, ...patch }));
  }, []);

  const canSubmit = useMemo(() => {
    return (
      formData.name.trim() !== '' &&
      formData.serialNumber.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.type.trim() !== ''
    );
  }, [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (facilityId == null || !canSubmit) {
      return;
    }
    setSaving(true);

    if (isEditing && formData.id != null) {
      const payload = {
        id: formData.id,
        name: formData.name.trim(),
        serialNumber: formData.serialNumber.trim(),
        description: formData.description.trim(),
        type: formData.type.trim(),
      };
      void api.put(`/facility-equipment/${formData.id}`, payload).then(
        () => {
          setSaving(false);
          alertStore.success('Equipment updated successfully');
          onClose(true);
        },
        () => {
          setSaving(false);
          alertStore.error('Changes failed to save');
        }
      );
      return;
    }

    void api
      .post('/facility-equipment', {
        facilityId,
        name: formData.name.trim(),
        serialNumber: formData.serialNumber.trim(),
        description: formData.description.trim(),
        type: formData.type.trim(),
      })
      .then(
        () => {
          setSaving(false);
          alertStore.success('New Equipment was added successfully');
          onClose(true);
        },
        () => {
          setSaving(false);
          alertStore.error('An error occurred while adding new equipment');
        }
      );
  };

  const handleClose = () => {
    if (!saving) {
      onClose(false);
    }
  };

  return (
    <Modal show={modalOpen} onHide={handleClose} backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit' : 'Add'} Equipment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              required
              value={formData.name}
              disabled={saving}
              onChange={(e) => setField({ name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Serial Number</Form.Label>
            <Form.Control
              required
              value={formData.serialNumber}
              disabled={saving}
              onChange={(e) => setField({ serialNumber: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              required
              value={formData.description}
              disabled={saving}
              onChange={(e) => setField({ description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Control
              required
              value={formData.type}
              disabled={saving}
              onChange={(e) => setField({ type: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !canSubmit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditModal;
